import { gql, useMutation, useQuery } from "@apollo/client";
import { useState } from "react";
import { Button, Form, Modal, Message } from "semantic-ui-react";

const generoOptions = [
  { key: "Masculino", value: "Masculino", text: "Masculino" },
  { key: "Femenino", value: "Femenino", text: "Femenino" },
];

const StudentConversionModal = ({
  open,
  adminId,
  basicInput,
  onCompleted,
  onCancel,
}) => {
  const [genero, setGenero] = useState("");
  const [direccion, setDireccion] = useState("");
  const [telefono, setTelefono] = useState("");
  const [fechaNacimiento, setFechaNacimiento] = useState("");
  const [idTipoDocumento, setIdTipoDocumento] = useState("");
  const [fechaDocumentoExpedicion, setFechaDocumentoExpedicion] = useState("");
  const [valorProcedencia, setValorProcedencia] = useState("");
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState(null);

  const { data: catalogos, loading: loadingCatalogos } = useQuery(
    CATALOGOS_ESTUDIANTE,
    {
      skip: !open,
    },
  );

  const [actualizarAdmin] = useMutation(UPDATE_ADMIN_GROUP);
  const [actualizarPerfil] = useMutation(UPDATE_STUDENT_PROFILE);

  const isDisabled =
    !genero ||
    !direccion.trim() ||
    !fechaNacimiento ||
    !idTipoDocumento ||
    !fechaDocumentoExpedicion;

  const handleConfirm = async () => {
    setLoading(true);
    setError(null);
    try {
      const resultAdmin = await actualizarAdmin({
        variables: {
          input: {
            id: adminId,
            idGrupoUsuario: "2",
          },
        },
      });

      const grupoActualizado =
        resultAdmin?.data?.actualizarAdmin?.multiGrupoUsuario?.id;
      if (String(grupoActualizado) !== "2") {
        throw new Error(
          "El servidor no actualizó el perfil. Verifica que el backend esté corriendo con el código más reciente.",
        );
      }

      const profileInput = {
        id: adminId,
        genero,
        direccion,
        fechaNacimiento,
        idTipoDocumento,
        fechaDocumentoExpedicion,
      };
      if (telefono) profileInput.telefono = telefono;
      if (valorProcedencia) profileInput.valorProcedencia = valorProcedencia;

      await actualizarPerfil({ variables: { input: profileInput } });

      onCompleted();
    } catch (err) {
      setError(
        err?.graphQLErrors?.[0]?.message ||
          err?.message ||
          "Ha ocurrido un error al convertir el perfil.",
      );
    } finally {
      setLoading(false);
    }
  };

  return (
    <Modal size="small" open={open} dimmer="inverted">
      <Modal.Header>Convertir usuario a Estudiante</Modal.Header>
      <Modal.Content>
        <Message info>
          <Message.Header>Información requerida</Message.Header>
          <p>
            Este usuario pasará a ser Estudiante y perderá acceso al panel de
            administración. Complete los siguientes datos necesarios para el
            perfil estudiantil.
          </p>
        </Message>

        {error && (
          <Message negative>
            <p>{error}</p>
          </Message>
        )}

        <Form loading={loadingCatalogos}>
          <Form.Dropdown
            label="Género *"
            selection
            placeholder="Seleccione género"
            value={genero}
            options={generoOptions}
            onChange={(e, { value }) => setGenero(value)}
          />

          <Form.Dropdown
            label="Procedencia"
            selection
            clearable
            placeholder="Seleccione procedencia (opcional)"
            value={valorProcedencia}
            loading={loadingCatalogos}
            options={catalogos?.procedencia || []}
            onChange={(e, { value }) => setValorProcedencia(value)}
          />

          <Form.Input
            label="Dirección *"
            placeholder="Ingrese la dirección"
            value={direccion}
            onChange={(e, { value }) => setDireccion(value)}
          />

          <Form.Input
            label="Teléfono"
            placeholder="Ingrese el teléfono (opcional)"
            value={telefono}
            onChange={(e, { value }) => setTelefono(value)}
          />

          <Form.Input
            label="Fecha de nacimiento *"
            type="date"
            value={fechaNacimiento}
            onChange={(e, { value }) => setFechaNacimiento(value)}
          />

          <Form.Dropdown
            label="Tipo de documento *"
            selection
            placeholder="Seleccione tipo de documento"
            value={idTipoDocumento}
            loading={loadingCatalogos}
            options={catalogos?.tipoDocumento || []}
            onChange={(e, { value }) => setIdTipoDocumento(value)}
          />

          <Form.Input
            label="Fecha de expedición del documento *"
            type="date"
            value={fechaDocumentoExpedicion}
            onChange={(e, { value }) => setFechaDocumentoExpedicion(value)}
          />
        </Form>
      </Modal.Content>
      <Modal.Actions>
        <Button
          style={{ color: "white", backgroundColor: "#045A73" }}
          loading={loading}
          disabled={isDisabled || loading}
          onClick={handleConfirm}
          content="Confirmar cambio"
        />
        <Button
          style={{ backgroundColor: "#b0193f", color: "white" }}
          disabled={loading}
          onClick={onCancel}
          content="Cancelar"
        />
      </Modal.Actions>
    </Modal>
  );
};

const CATALOGOS_ESTUDIANTE = gql`
  query catalogosEstudiante {
    procedencia: multiValorParametros(
      filtro: { estado: ACTIVO, idParametro: 1 }
    ) {
      value: id
      text: valorParametro
      key: id
    }
    tipoDocumento: multiValorParametros(
      filtro: { estado: ACTIVO, idParametro: 5 }
    ) {
      value: id
      text: valorParametro
      key: id
    }
  }
`;

const UPDATE_ADMIN_GROUP = gql`
  mutation updateAdminGroup($input: ActualizarMultiUsuarioAdminInput!) {
    actualizarAdmin(input: $input) {
      id
      multiGrupoUsuario {
        id
        nombre
      }
      estado
    }
  }
`;

const UPDATE_STUDENT_PROFILE = gql`
  mutation updateStudentProfile($input: ActualizarMultiUsuarioInput!) {
    actualizarMultiUsuario(input: $input) {
      id
      genero
      direccion
      telefono
      fechaNacimiento
      tipoDocumento {
        id
        valorParametro
      }
      valorProcedencia {
        id
        valorParametro
      }
    }
  }
`;

export default StudentConversionModal;
